import { useState } from 'react';

import { useNavigate } from 'react-router';

import { IconArrowLeft } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  getPlanAndAssignmentsOptions,
  getQuizDetailsOptions,
  type SystemPlan,
} from '@cfa/pathway-fe-shared/xp';
import { Button, Card } from '@cfa/react-components';
import { LoadingIndicator } from '@cfa/react-core';
import { MessageBlock } from '@cfa/react-core';

import { isValidPlanId } from '@/utils/isValidPlanId';
import { itemByLanguage } from '@/utils/language';

import QuizPreviewCard from '@/components/Cards/QuizPreviewCard';
import DocumentPreview from '@/components/DocumentPreview/DocumentPreview';
import PathwayModal from '@/components/popups/PathwayModal';

import { routes } from '@/constants/routes';

import { BaseReferenceDTO } from '@/services/content-api-types';

import PlanStepContainer from '@/routes/TrainingPlans/BuildPlans/EditablePlanItems/PlanStepContainer';
import Section from '@/routes/TrainingPlans/BuildPlans/EditablePlanItems/Section';

interface PreviewTemplateModalStepProps {
  onCopy: (id: string) => void;
  onTemplateBackButtonClick: () => void;
  plan: SystemPlan;
  onAdd?: (template: SystemPlan) => void;
  isLoading?: boolean;
  onViewProgress?: () => void;
}

const PreviewTemplateModalStep: React.FC<PreviewTemplateModalStepProps> = ({
  onCopy,
  onTemplateBackButtonClick,
  plan,
  onAdd,
  isLoading = false,
  onViewProgress,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isAlreadyAdded = plan.readonlySubscriptionSettings?.optedIn === true;
  const [isResourcePreview, setIsResourcePreview] = useState(false);
  const [previewDocument, setPreviewDocument] = useState<BaseReferenceDTO>({});
  const [previewQuizId, setPreviewQuizId] = useState('');

  const {
    data: templatePlanAndAssignments,
    isSuccess,
    isFetching,
  } = useQuery({
    ...getPlanAndAssignmentsOptions({ path: { planId: plan.id } }),
    enabled: !!plan.id && isValidPlanId(plan.id),
  });

  const {
    data: quizData,
    error: quizError,
    isSuccess: isQuizSuccess,
    isFetching: isQuizFetching,
  } = useQuery({
    ...getQuizDetailsOptions({ path: { quizId: previewQuizId } }),
    enabled: !!previewQuizId,
  });

  // keep template for content api preview and no flash in XP (https://cfacorp.atlassian.net/browse/PATHWAY-2063)
  const templatePlan =
    isSuccess && !isFetching ? templatePlanAndAssignments.plan : null;

  const sections = templatePlan?.content?.sections;
  const steps = templatePlan?.content?.steps;

  const planNameEn = itemByLanguage(plan.name, '', 'English');

  const getComplianceDescription = () => {
    if (!planNameEn || plan.category !== 'compliance') return null;

    const name = planNameEn.toLowerCase();
    if (name.includes('food safety')) {
      return t('TrainingPlans.templates.complianceDescriptions.foodSafety');
    }
    if (name.includes('pci') && name.includes('cybersecurity')) {
      return t(
        'TrainingPlans.templates.complianceDescriptions.pciCybersecurity',
      );
    }
    if (name.includes('gift card')) {
      return t('TrainingPlans.templates.complianceDescriptions.giftCard');
    }
    if (name.includes('harassment')) {
      return t('TrainingPlans.templates.complianceDescriptions.harassment');
    }
    return null;
  };

  const complianceDescription = getComplianceDescription();

  const modalTitle = isResourcePreview
    ? previewDocument.name
    : previewQuizId
      ? itemByLanguage(quizData?.name)
      : itemByLanguage(plan.name);

  const onBackButtonClick = () => {
    setIsResourcePreview(false);
    setPreviewDocument({});
    setPreviewQuizId('');
  };

  const handleResourceOrQuizPreview = (
    documentOrQuiz: BaseReferenceDTO | string,
  ) => {
    if (typeof documentOrQuiz === 'string') {
      setPreviewQuizId(documentOrQuiz);
    } else {
      setIsResourcePreview(true);
      setPreviewDocument(documentOrQuiz);
    }
  };

  const handleBackButtonClick = () => {
    if (isResourcePreview || previewQuizId) {
      onBackButtonClick();
    } else {
      onTemplateBackButtonClick();
    }
  };

  const getButtonText = () => {
    if (plan.readonly) {
      return isAlreadyAdded ? t('Button.added') : t('Button.add');
    }
    return t('Button.copy');
  };

  const handleButtonClick = () => {
    if (plan.id) {
      if (plan.readonly) {
        if (onAdd) {
          onAdd(plan);
        } else {
          navigate(routes.plans.addReadOnlyPlan({ planId: plan.id }));
        }
      } else {
        onCopy(plan.id);
      }
    }
  };

  const showComplianceBanner =
    Boolean(complianceDescription) && !isResourcePreview;
  const showReadonlyBanner =
    plan.readonly && !isResourcePreview && !previewQuizId;

  return (
    <>
      <PathwayModal.Header>
        <div className="absolute top-4 -left-6">
          <Button
            className="-translate-2.5! pl-0!"
            color="secondary"
            onClick={handleBackButtonClick}
            size="md"
            variant="text"
          >
            <IconArrowLeft />
          </Button>
        </div>
        <PathwayModal.Title>{t('TrainingPlans.preview')}</PathwayModal.Title>
        <p className="text-center text-3xl">{modalTitle}</p>
      </PathwayModal.Header>
      <PathwayModal.Body>
        {(showComplianceBanner || showReadonlyBanner) && (
          <div className="mb-5 flex flex-col gap-4">
            {showComplianceBanner && (
              <MessageBlock.Root severity="info" className="w-full! text-start">
                {complianceDescription}
              </MessageBlock.Root>
            )}
            {showReadonlyBanner && (
              <MessageBlock.Root severity="info" className="w-full! text-start">
                {t('TrainingPlans.templates.previewInfo.line2')}
              </MessageBlock.Root>
            )}
          </div>
        )}
        {isResourcePreview && (
          <DocumentPreview
            previewDocument={previewDocument}
            handleGoBack={onBackButtonClick}
            isTemplatePreview
          />
        )}
        {previewQuizId && isQuizFetching && (
          <div className="flex h-full items-center justify-center py-16">
            <LoadingIndicator size="lg" />
          </div>
        )}
        {previewQuizId && !isQuizFetching && (isQuizSuccess || quizError) && (
          <div>
            <QuizPreviewCard quiz={quizData} quizError={quizError} />
          </div>
        )}
        {!previewQuizId && !isResourcePreview && !templatePlan && (
          <div className="flex h-full items-center justify-center py-16">
            <LoadingIndicator size="lg" />
          </div>
        )}
        {!previewQuizId && !isResourcePreview && templatePlan && (
          <div className="mx-auto flex w-full flex-col gap-6 pb-8">
            {steps?.map(step => (
              <Card className="no-hover px-4!" key={step.id}>
                <PlanStepContainer
                  className="no-drag"
                  isPreview
                  step={step}
                  handleResourceOrQuizPreview={handleResourceOrQuizPreview}
                />
              </Card>
            ))}
            {sections?.map((section, index) => (
              <Section
                index={index}
                isPreview={true}
                key={section.id}
                section={section}
                handleResourceOrQuizPreview={handleResourceOrQuizPreview}
                className={'no-hover'}
              />
            ))}
          </div>
        )}
      </PathwayModal.Body>
      {!isResourcePreview && !previewQuizId && (
        <PathwayModal.Footer>
          <PathwayModal.SecondaryButton
            color="secondary"
            testId="copyTemplateFromPreviewButton"
            isDisabled={plan.readonly && isAlreadyAdded}
            isPending={isLoading}
            onPress={handleButtonClick}
            variant="filled"
          >
            {getButtonText()}
          </PathwayModal.SecondaryButton>
          {plan.readonly && isAlreadyAdded && onViewProgress && (
            <PathwayModal.PrimaryButton
              color="secondary"
              data-testid="viewProgressButton"
              onPress={onViewProgress}
              variant="filled"
            >
              {t('Button.viewProgress')}
            </PathwayModal.PrimaryButton>
          )}
        </PathwayModal.Footer>
      )}
    </>
  );
};

export default PreviewTemplateModalStep;
